import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import {
  getX, hasDateValues, daysToDate, smartYMin,
  renderXAxisLabel, renderYAxisLabel, yLabelNeedsWrap,
  truncateLabel, xTickHeight, xTicksNeedRotation, tickLabelLines,
  formatChartNumber, xLabelDisplay,
} from './chartUtils';

function AreaChartRenderer({ data, width, height, xLabel, yLabel }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0 || !width || !height) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (data.length < 2) {
      svg.attr('width', width).attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .append('text')
        .attr('x', width / 2).attr('y', height / 2)
        .attr('text-anchor', 'middle').attr('font-size', '13px')
        .attr('font-family', 'Roboto, sans-serif').attr('fill', '#757575')
        .text('Area charts need at least 2 data points. Try a bar chart or KPI card.');
      return;
    }

    const isDate = hasDateValues(data);
    const baseLeft = isDate ? 110 : 70;
    const fontSize = 11;
    const charWidth = fontSize * 0.55;
    const rawLabels = data.map((d) => String(getX(d)));
    // ISO date buckets read as "Apr 2026"; any other label is shown as sent.
    const showX = xLabelDisplay(rawLabels);
    const shownLabels = rawLabels.map(showX);
    const estBand = (width - baseLeft - 20) / Math.max(data.length, 1);
    const maxCharsPerLine = Math.max(8, Math.floor(estBand * 0.9 / charWidth));
    const rotateX = !isDate && xTicksNeedRotation(shownLabels, estBand);

    const xLines = rotateX
      ? shownLabels.map((l) => [truncateLabel(l)])
      : isDate
        ? shownLabels.map((l) => [l])
        : shownLabels.map((l) => tickLabelLines(l, maxCharsPerLine));
    const linesByLabel = new Map(rawLabels.map((l, i) => [l, xLines[i]]));

    const tickH = rotateX
      ? xTickHeight(xLines.map((ls) => ls[0]), fontSize, true)
      : fontSize + 4 + (Math.max(1, ...xLines.map((ls) => ls.length)) - 1) * 14;
    const marginBottom = Math.max(60, tickH + (xLabel ? 24 : 8));
    const wrapsY = yLabelNeedsWrap(yLabel, height - 20 - marginBottom);
    const margin = { top: 20, right: 20, bottom: marginBottom, left: baseLeft + (wrapsY ? 14 : 0) };
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;
    const g = svg.attr('width', width).attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const labels = data.map((d) => getX(d));
    const x = d3.scalePoint().domain(labels).range([0, w]).padding(0.5);
    const yMin = isDate ? d3.min(data, (d) => d.value) * 0.95 : smartYMin(data);
    const yMax = d3.max(data, (d) => d.value) * 1.05;
    const y = d3.scaleLinear().domain([yMin, yMax]).range([h, 0]);

    const xTickText = g.append('g').attr('transform', `translate(0,${h})`)
      .call(d3.axisBottom(x).tickFormat(showX))
      .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');
    if (rotateX) {
      xTickText
        .text((d) => (linesByLabel.get(String(d)) || [String(d)])[0])
        .attr('transform', 'rotate(-45)').attr('text-anchor', 'end')
        .attr('dx', '-0.3em').attr('dy', '0.3em');
    } else {
      xTickText.each(function (d) {
        const t = d3.select(this);
        const lines = linesByLabel.get(String(d)) || [String(d)];
        t.text(null).attr('dy', null);
        lines.forEach((line, i) => {
          t.append('tspan').attr('x', 0).attr('dy', i === 0 ? '0.71em' : '1.1em').text(line);
        });
      });
    }
    xTickText.append('title').text(showX);

    const yAxisFormat = isDate ? (v) => daysToDate(v) : formatChartNumber;
    g.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(yAxisFormat))
      .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');

    const area = d3.area().x((d) => x(getX(d))).y0(y(yMin)).y1((d) => y(d.value)).curve(d3.curveMonotoneX);
    const line = d3.line().x((d) => x(getX(d))).y((d) => y(d.value)).curve(d3.curveMonotoneX);

    g.append('path').datum(data).attr('fill', '#4A90D9').attr('fill-opacity', 0.15).attr('d', area);
    g.append('path').datum(data).attr('fill', 'none').attr('stroke', '#4A90D9').attr('stroke-width', 2).attr('d', line);

    g.selectAll('circle').data(data).enter().append('circle')
      .attr('cx', (d) => x(getX(d)))
      .attr('cy', (d) => y(d.value))
      .attr('r', 4).attr('fill', '#fff').attr('stroke', '#4A90D9').attr('stroke-width', 2);

    // Value labels above each point
    g.selectAll('.point-label').data(data).enter().append('text')
      .attr('x', (d) => x(getX(d)))
      .attr('y', (d) => y(d.value) - 10)
      .attr('text-anchor', 'middle').attr('font-size', '11px')
      .attr('font-family', 'Roboto, sans-serif').attr('fill', '#212121')
      .text((d) => isDate ? daysToDate(d.value) : formatChartNumber(d.value));

    renderXAxisLabel(g, xLabel, w, h, tickH);
    renderYAxisLabel(g, yLabel, h, margin.left);
  }, [data, width, height, xLabel, yLabel]);

  return <svg ref={svgRef} />;
}

export default AreaChartRenderer;
